import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

import { trackEvent as realTrackEvent } from "./analytics/events";

export function trackEvent(eventName: string, data: Record<string, any> = {}) {
  realTrackEvent(eventName, data);
}

export function cleanErrorMessage(msg: string, defaultMsg: string): string {
  if (!msg) return defaultMsg;
  
  // Map system error codes to custom friendly Indonesian messages
  const systemPhrases: Record<string, string> = {
    invalid_input: 'Coba periksa lagi apa ada bahan atau data yang salah ya, Mama.',
    parse_error: 'AI sedang sedikit kewalahan membaca resep. Coba klik buat lagi ya.',
    validation_error: 'AI kurang paham bahan atau menu yang dicarikan. Coba lebih spesifik.',
    api_error: 'Koneksi ke AI terputus. Coba periksa koneksi internet ya, Mama.',
    quota_exceeded: 'Sistem sedang luar biasa sibuk. Mohon tunggu sejenak lalu coba lagi ya, Mama.',
    safety_block: 'Pencarian ini tidak diizinkan demi keamanan. Mohon gunakan bahan makanan biasa saja.',
    timeout: 'AI butuh waktu terlalu lama untuk menjawab. Silakan coba kembali.',
    api_key_invalid: 'Kunci API Gemini belum diatur atau tidak valid. Silakan pasang GEMINI_API_KEY di menu Settings (ikon roda gigi) di pojok kanan atas.'
  };

  const cleanKey = msg.trim();
  if (systemPhrases[cleanKey]) {
    return systemPhrases[cleanKey];
  }

  const lowerMsg = msg.toLowerCase();
  
  // Check if it's a technical/leak error (stacktrace, JSON parsing, fetch endpoints, HTTP errors, etc.)
  const isTech = 
    lowerMsg.includes('http') ||
    lowerMsg.includes('zoderror') || // more specific than 'error '
    lowerMsg.includes('parse') ||
    lowerMsg.includes('failed to fetch') ||
    lowerMsg.includes('syntax') ||
    lowerMsg.includes('generativeai') ||
    lowerMsg.includes('undefined') ||
    lowerMsg.includes('typeerror') ||
    lowerMsg.includes('unexpected') ||
    lowerMsg.includes('stack') ||
    msg.length > 150; // increased from 90 to allow our detailed validation messages

  if (isTech) {
    // If it mentions anything key-related or unauthorized, prompt for API Key
    if (lowerMsg.includes('key') || lowerMsg.includes('unregistered') || lowerMsg.includes('api_key') || lowerMsg.includes('credential')) {
      return 'Kunci API Gemini belum diatur atau tidak valid. Silakan pasang GEMINI_API_KEY di menu Settings (ikon roda gigi) di pojok kanan atas.';
    }
    return defaultMsg;
  }
  
  return msg;
}

export function categorizeIngredientLocal(nameConfig: string): 'karbo' | 'protein' | 'sayur' | 'buah' | 'lemak' {
  const name = nameConfig.toLowerCase();
  
  const karboKeywords = [
    'beras', 'nasi', 'kentang', 'singkong', 'ubi', 'jagung', 'roti', 'gandum', 'oat', 'pasta', 'mi', 'mie', 
    'talas', 'sagu', 'tepung', 'bihun', 'makaroni', 'bubur', 'semolina', 'crushed wheat', 'biskuit', 'crack'
  ];
  
  const proteinKeywords = [
    'ayam', 'daging', 'sapi', 'kambing', 'bebek', 'ikan', 'salmon', 'tuna', 'kembung', 'kakap', 'gurame', 
    'nila', 'lele', 'udang', 'cumi', 'kerang', 'kepiting', 'egg', 'eggs', 'telur', 'tofu', 'tahu', 'tempe', 
    'edamame', 'hati', 'limpa', 'paru', 'bakso', 'sosis', 'kornet', 'kacang', 'polong', 'kedelai', 'merah', 'hijau'
  ];
  
  const sayurKeywords = [
    'bayam', 'brokoli', 'wortel', 'labu siam', 'jipang', 'sawi', 'pakcoy', 'bokchoy', 'kangkung', 'buncis', 
    'kacang panjang', 'tomat', 'timun', 'mentimun', 'terong', 'kol', 'kubis', 'kembang kol', 'seledri', 
    'daun bawang', 'bawang', 'jamur', 'labu kuning', 'oyong', 'gambas', 'lobak', 'rebung', 'asparagus', 
    'paria', 'pare', 'kemangi', 'sayur', 'daun', 'seledry', 'kailan'
  ];
  
  const buahKeywords = [
    'pisang', 'pepaya', 'alpukat', 'jeruk', 'apel', 'mangga', 'buah naga', 'strawberry', 'stroberi', 
    'blueberry', 'pear', 'pir', 'kiwi', 'melon', 'semangka', 'anggur', 'nanas', 'sirsak', 'kurma', 'kismis', 'buah'
  ];
  
  const lemakKeywords = [
    'minyak', 'mentega', 'butter', 'margarin', 'santan', 'keju', 'ub', 'cheese', 'olive', 'zaitun', 
    'kemiri', 'wijen', 'lemak', 'gajih', 'canola', 'coconut oil', 'lard', 'tallow'
  ];

  for (const kw of lemakKeywords) {
    if (name.includes(kw)) return 'lemak';
  }
  for (const kw of buahKeywords) {
    if (name.includes(kw)) return 'buah';
  }
  for (const kw of sayurKeywords) {
    if (name.includes(kw)) return 'sayur';
  }
  for (const kw of proteinKeywords) {
    if (name.includes(kw)) return 'protein';
  }
  for (const kw of karboKeywords) {
    if (name.includes(kw)) return 'karbo';
  }

  if (name.includes('susu') || name.includes('yoghurt') || name.includes('milky')) {
    return 'protein';
  }
  
  return 'sayur';
}

export function getIngredientEmoji(nameConfig: string, categoryConfig?: string): string {
  const name = nameConfig.toLowerCase();
  const cat = (categoryConfig || categorizeIngredientLocal(nameConfig)).toLowerCase();

  // 1. Specific item-name pattern matches first for granular emojis
  // Susu
  if (name.includes('susu') || name.includes('milk') || name.includes('uht') || name.includes('yoghurt') || name.includes('yogurt')) {
    return '🥛';
  }
  // Telur
  if (name.includes('telur') || name.includes('egg') || name.includes('puyuh')) {
    return '🥚';
  }
  // Kacang-kacangan
  if (name.includes('kacang') || name.includes('polong') || name.includes('kedelai') || name.includes('almond') || name.includes('mede') || name.includes('peanut')) {
    return '🫘';
  }
  // Sosis / Bakso / Nugget
  if (name.includes('sosis') || name.includes('sausage') || name.includes('bakso') || name.includes('nugget') || name.includes('kornet') || name.includes('ham') || name.includes('smoke')) {
    return '🌭';
  }
  // Ikan
  if (name.includes('ikan') || name.includes('salmon') || name.includes('tuna') || name.includes('kembung') || name.includes('kakap') || name.includes('gurame') || name.includes('nila') || name.includes('lele') || name.includes('dori') || name.includes('bawal')) {
    return '🐟';
  }
  // Ayam
  if (name.includes('ayam') || name.includes('chicken') || name.includes('bebek') || name.includes('poultry')) {
    return '🍗';
  }
  // Daging Sapi / Kambing
  if (name.includes('sapi') || name.includes('beef') || name.includes('kambing') || name.includes('daging') || name.includes('lamb') || name.includes('paru') || name.includes('limpa') || name.includes('hati')) {
    return '🥩';
  }
  // Udang / Cumi / Kepiting / Kerang
  if (name.includes('udang') || name.includes('shrimp') || name.includes('prawn') || name.includes('lobster')) {
    return '🍤';
  }
  if (name.includes('cumi') || name.includes('squid') || name.includes('gurita')) {
    return '🦑';
  }
  if (name.includes('kepiting') || name.includes('crab')) {
    return '🦀';
  }
  if (name.includes('kerang') || name.includes('clams') || name.includes('shellfish')) {
    return '🐚';
  }

  // Karbo-specific
  if (name.includes('kentang') || name.includes('singkong') || name.includes('ubi') || name.includes('potato') || name.includes('talas')) {
    return '🥔';
  }
  if (name.includes('jagung') || name.includes('corn')) {
    return '🌽';
  }
  if (name.includes('roti') || name.includes('gandum') || name.includes('bread') || name.includes('biskuit')) {
    return '🍞';
  }
  if (name.includes('mie') || name.includes('mi') || name.includes('pasta') || name.includes('spaghetti') || name.includes('noodle')) {
    return '🍜';
  }
  if (name.includes('beras') || name.includes('nasi') || name.includes('rice') || name.includes('bubur')) {
    return '🍚';
  }

  // Buah-specific
  if (name.includes('pisang') || name.includes('banana')) {
    return '🍌';
  }
  if (name.includes('pepaya') || name.includes('papaya') || name.includes('mangga') || name.includes('mango')) {
    return '🥭';
  }
  if (name.includes('alpukat') || name.includes('avocado')) {
    return '🥑';
  }
  if (name.includes('jeruk') || name.includes('orange') || name.includes('lemon') || name.includes('citrus')) {
    return '🍊';
  }
  if (name.includes('apel') || name.includes('apple')) {
    return '🍎';
  }
  if (name.includes('stroberi') || name.includes('strawberry')) {
    return '🍓';
  }
  if (name.includes('anggur') || name.includes('grape')) {
    return '🍇';
  }
  if (name.includes('semangka') || name.includes('watermelon')) {
    return '🍉';
  }
  if (name.includes('melon')) {
    return '🍈';
  }
  if (name.includes('nanas') || name.includes('pineapple')) {
    return '🍍';
  }

  // Sayur-specific
  if (name.includes('wortel') || name.includes('carrot')) {
    return '🥕';
  }
  if (name.includes('brokoli') || name.includes('broccoli')) {
    return '🥦';
  }
  if (name.includes('tomat') || name.includes('tomato')) {
    return '🍅';
  }
  if (name.includes('jamur') || name.includes('mushroom')) {
    return '🍄';
  }
  if (name.includes('bawang') || name.includes('garlic') || name.includes('onion')) {
    return '🧄';
  }
  if (name.includes('cabe') || name.includes('cabai') || name.includes('chili')) {
    return '🌶️';
  }

  // Lemak-specific
  if (name.includes('keju') || name.includes('cheese')) {
    return '🧀';
  }
  if (name.includes('butter') || name.includes('mentega') || name.includes('margarin') || name.includes('ub')) {
    return '🧈';
  }
  if (name.includes('santan') || name.includes('kelapa') || name.includes('coconut')) {
    return '🥥';
  }
  if (name.includes('minyak') || name.includes('oil')) {
    return '🫗';
  }

  // 2. Category-based fallback
  if (cat.includes('protein')) return '🥩';
  if (cat.includes('sayur')) return '🥦';
  if (cat.includes('buah')) return '🍎';
  if (cat.includes('karbo')) return '🍚';
  if (cat.includes('lemak')) return '🧈';

  return '🥫';
}

export function standardizeAmountAndUnit(amountInput: string): { amount: string; value?: number; unit?: string } {
  const trimmed = amountInput.trim().toLowerCase();
  if (!trimmed) {
    return { amount: 'secukupnya', value: undefined, unit: 'secukupnya' };
  }

  if (trimmed === 'secukupnya' || trimmed === 'secukupny' || trimmed === 'scukupnya' || trimmed === 'sckp' || trimmed === 'ada' || trimmed === 'stok ada') {
    return { amount: 'secukupnya', value: undefined, unit: 'secukupnya' };
  }

  const match = trimmed.match(/^([\d\/\.,\s]+)?\s*(.*)$/);
  
  let valStr = match ? (match[1] || '').trim() : '';
  let unitStr = match ? (match[2] || '').trim() : '';

  let numericValue: number | undefined = undefined;
  if (valStr) {
    valStr = valStr.replace(',', '.');
    
    if (valStr.includes('/')) {
      const parts = valStr.split('/');
      if (parts.length === 2) {
        const num = parseFloat(parts[0]);
        const den = parseFloat(parts[1]);
        if (!isNaN(num) && !isNaN(den) && den !== 0) {
          numericValue = num / den;
        }
      }
    } else {
      const parsed = parseFloat(valStr);
      if (!isNaN(parsed)) {
        numericValue = parsed;
      }
    }
  }

  let standardizedUnit = unitStr;
  if (!unitStr) {
    standardizedUnit = '';
  } else {
    const unitClean = unitStr.replace(/[\.\s]/g, '').toLowerCase();
    if (['gram', 'gr', 'grm', 'g'].includes(unitClean)) {
      standardizedUnit = 'gr';
    } else if (['kilogram', 'kg', 'kilo', 'kilogr'].includes(unitClean)) {
      standardizedUnit = 'kg';
    } else if (['mililiter', 'ml', 'mili', 'milli'].includes(unitClean)) {
      standardizedUnit = 'ml';
    } else if (['liter', 'lt', 'l', 'ltr'].includes(unitClean)) {
      standardizedUnit = 'l';
    } else if (['butir', 'btr', 'bt'].includes(unitClean)) {
      standardizedUnit = 'butir';
    } else if (['buah', 'bh'].includes(unitClean)) {
      standardizedUnit = 'buah';
    } else if (['lembar', 'lbr'].includes(unitClean)) {
      standardizedUnit = 'lembar';
    } else if (['biji', 'bj'].includes(unitClean)) {
      standardizedUnit = 'biji';
    } else if (['siung', 'sg', 'sung'].includes(unitClean)) {
      standardizedUnit = 'siung';
    } else if (['mangkuk', 'mangkok', 'mngk'].includes(unitClean)) {
      standardizedUnit = 'mangkuk';
    } else if (['sendokteh', 'sdt', 'tsp'].includes(unitClean)) {
      standardizedUnit = 'sdt';
    } else if (['sendokmakan', 'sdm', 'tbsp'].includes(unitClean)) {
      standardizedUnit = 'sdm';
    } else if (['ikat', 'ikt'].includes(unitClean)) {
      standardizedUnit = 'ikat';
    } else if (['bungkus', 'bks'].includes(unitClean)) {
      standardizedUnit = 'bungkus';
    }
  }

  let finalAmount = '';
  if (numericValue !== undefined) {
    const formattedVal = Number(numericValue.toFixed(2)).toString();
    finalAmount = standardizedUnit ? `${formattedVal} ${standardizedUnit}` : formattedVal;
  } else {
    finalAmount = standardizedUnit || 'secukupnya';
  }

  return {
    amount: finalAmount,
    value: numericValue,
    unit: standardizedUnit || undefined
  };
}

export function oklchToRgbVal(l: number, c: number, h: number, alpha?: number | string): string {
  const hRad = (h * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);

  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.2914855480 * b;

  const lCube = l_ * l_ * l_;
  const mCube = m_ * m_ * m_;
  const sCube = s_ * s_ * s_;

  let r = +4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube;
  let g = -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube;
  let bVal = -0.0041960863 * lCube - 0.7034186147 * mCube + 1.7076147010 * sCube;

  const fn = (x: number) => {
    if (x > 0.0031308) {
      return 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
    }
    return 12.92 * x;
  };

  r = Math.max(0, Math.min(1, fn(r)));
  g = Math.max(0, Math.min(1, fn(g)));
  bVal = Math.max(0, Math.min(1, fn(bVal)));

  const rInt = Math.round(r * 255);
  const gInt = Math.round(g * 255);
  const bInt = Math.round(bVal * 255);

  if (alpha !== undefined) {
    let aVal = 1;
    if (typeof alpha === 'string') {
      if (alpha.endsWith('%')) {
        aVal = parseFloat(alpha) / 100;
      } else {
        aVal = parseFloat(alpha);
      }
    } else {
      aVal = alpha;
    }
    return `rgba(${rInt}, ${gInt}, ${bInt}, ${aVal})`;
  }
  return `rgb(${rInt}, ${gInt}, ${bInt})`;
}



